/* istanbul ignore file */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { sanitize } from 'sanitizer';

import { fetchWishes, storeWish } from 'actions/account/account';
import css from 'styles/zen/account/wishes.scss';

const wishObject = {
  SHOE: {
    wishName: 'Product',
    examples: [
      'Jordan 1 Retro High Travis Scott',
      'Nike x Parra Woven Warm Up Tracksuit',
      'adidas Yeezy Boost 350 V2 Clay'
    ]
  },

  EVENT: {
    wishName: 'Event',
    examples: [
      'Golden State Warriors NBA Playoff game',
      '2019 Lollapalooza Chicago',
      'Beyoncé: OTR II Concert'
    ]
  },

  WILDCARD: {
    wishName: 'Wild Card',
    examples: [
      'Disney World Trip for my family',
      'Drums to set the world on fire',
      'Laptop for school'
    ]
  }
};

export class Wishes extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  };

  state = {
    SHOE: false,
    EVENT: false,
    WILDCARD: false
  };

  componentDidMount() {
    this.props.fetchWishes();
  }

  getActiveWishType = () => {
    const { state } = this;
    return Object.keys(state).find(wishType => state[wishType]);
  }

  onToggleWish = ({ currentTarget:{ dataset: { wishType } } }) => {
    this.setState(prevState => ({
      [wishType]: !prevState[wishType]
    }));

    // if another wish type was being editing, collapse it
    const prevWishType = this.getActiveWishType();
    if (prevWishType) {
      this.setState({
        [prevWishType]: false
      });
    }

  }

  onSaveWish = e => {
    e.preventDefault();

    const {
      currentTarget: {
        text,
        category
      }
    } = e;

    const sanitizedCategory = sanitize(category.value);
    const sanitizedText = sanitize(text.value);
    const wishType = this.getActiveWishType();

    this.setState({ [wishType]: false });
    this.props.storeWish({ category: sanitizedCategory, text: sanitizedText });
  }

  generateWishBlock = wishType => {
    const { wishes } = this.props;
    const { wishName, examples } = wishObject[wishType];
    const { wishText, isFulfilled } = wishes.find(wish => wish.wishCategory === wishType) || {};
    const article = /^[aeiou]$/i.test(wishName.charAt(0)) ? 'an' : 'a';

    // if wish is currently in Edit Mode
    if (this.state[wishType]) {
      return (
        <form
          method="post"
          onSubmit={this.onSaveWish}
          onReset={this.onToggleWish}>
          <div className={css.ctaContainer}>
            <button type="reset" className={css.cancel}>Cancel</button>
            <button type="submit" className={css.save}>Save Wish</button>
          </div>
          <input type="hidden" name="category" value={wishType} />
          {/* allowing of autofocus because its happening on user interaction (and not on pageload) */}
          <textarea
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            maxLength={280}
            name="text"
            defaultValue={wishText} />
        </form>
      );
    }

    return (
      <>
        <div className={css.ctaContainer}>
          <button
            type="button"
            className={css.primaryCTA}
            onClick={this.onToggleWish}
            data-wish-type={wishType}>
            { !wishText ? `Make ${article} ${wishName} Wish` : `Edit ${article} ${wishName} Wish`}
          </button>
        </div>

        { !wishText ? // display examples of wishes
          <div className={css.example}>
            <h3>{wishName} Wish examples:</h3>
            <ul>
              {examples.map((example, i) => <li key={i}>{example}</li>)}
            </ul>
          </div> : // display the saved wish
          <>
            <h3>Your {wishName} Wish:</h3>
            <p>{wishText}</p>
            { isFulfilled && <p className={css.granted}>Wish Granted!</p> }
          </>
        }
      </>
    );
  }

  render() {
    const { wishes } = this.props;
    const { testId } = this.context;

    if (!wishes) {
      return null;
    }

    return (
      <div data-test-id={testId('wishesContainer')} className={css.container}>
        <h2>Wishes</h2>
        <p>
          Make your dreams come true by making a wish! We want to know what you’ve been yearning for because we’ll be granting
          some wishes from our best customers. Enter <strong>ONE</strong> wish for each category because you never know when a wish can
          come true! <Link to="/c/wishes-terms">Read rules for entering.</Link>
        </p>
        <div className={css.wishes}>
          <div className={css.productWish}>
            { this.generateWishBlock('SHOE') }
          </div>

          <div className={css.eventWish}>
            { this.generateWishBlock('EVENT') }
          </div>

          <div className={css.wildcardWish}>
            { this.generateWishBlock('WILDCARD') }
          </div>
        </div>
      </div>
    );
  }
}

export const mapStateToProps = state => {
  const { account: { wishes } } = state;

  return {
    wishes
  };
};

export default connect(mapStateToProps, {
  fetchWishes,
  storeWish
})(Wishes);
